import { useEffect, useState, type FormEvent } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { BusinessRow, DeviceIdentity } from '@pos/local-db';
import { db } from '../../lib/db.ts';
import { useOnline } from '../../lib/use-online.ts';
import { loginEmployee, loginOwner, logout } from '../../services/session.ts';
import { Alert, Button, Card, Field, Input, errorMessage, PasswordInput } from '../../ui/index.tsx';
import { BusinessSwitcherButton } from './BusinessSwitcher.tsx';
import { BusinessLogo } from '../../app/BusinessLogo.tsx';
import { ForgotPassword } from './ForgotPassword.tsx';
import { PinPad } from '../../ui/PinPad.tsx';

type Mode =
  | { name: 'pick' }
  | { name: 'pin'; username?: string; displayName?: string }
  | { name: 'password'; email?: string; displayName?: string };

interface KnownUser {
  membershipId: string;
  displayName: string;
  kind: 'pin' | 'password';
  loginId: string;
}

/** Pantalla de ingreso: elegir usuario de este dispositivo o entrar con uno nuevo. */
export function LockScreen({
  device,
  business,
  deactivated,
}: {
  device: DeviceIdentity;
  business: BusinessRow;
  deactivated: boolean;
}) {
  const online = useOnline();
  const [mode, setMode] = useState<Mode>({ name: 'pick' });
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    deactivated ? 'Tu usuario fue desactivado. Hablá con el dueño del negocio.' : null,
  );

  // Una sesión de un usuario desactivado se cierra sola.
  useEffect(() => {
    if (deactivated) void logout();
  }, [deactivated]);

  // Usuarios que ya entraron en este dispositivo (y siguen activos).
  const known = useLiveQuery(async () => {
    const credentials = await db.credentials.where('business_id').equals(business.id).toArray();
    const users: KnownUser[] = [];
    for (const c of credentials) {
      const m = await db.memberships.get(c.membership_id);
      if (m?.active) users.push({ membershipId: m.id, displayName: m.display_name, kind: c.kind, loginId: c.login_id });
    }
    return users.sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  }, [business.id]);

  const go = (next: Mode) => {
    setError(null);
    setPin('');
    setPassword('');
    setMode(next);
  };

  const submit = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const submitPin = () => {
    const user = mode.name === 'pin' && mode.username ? mode.username : username;
    void submit(() => loginEmployee(device, user, pin));
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    const login = mode.name === 'password' && mode.email ? mode.email : email;
    void submit(() => loginOwner(device, login, password));
  };

  return (
    <div className="app-canvas flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <BusinessLogo name={business.name} logoPath={business.logo_path} className="mx-auto size-14 rounded-2xl text-2xl shadow-raised" />
          <h1 className="mt-3 text-xl font-semibold tracking-tight">{business.name}</h1>
          <p className="text-sm text-slate-500">{device.name}</p>
          <p className="mt-1 text-xs">
            <BusinessSwitcherButton />
          </p>
        </div>

        <Card className="space-y-4">
          {!online && (
            <Alert tone="warning">Sin conexión: pueden entrar los usuarios que ya ingresaron antes en este dispositivo.</Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}

          {mode.name === 'pick' && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">¿Quién sos?</p>
              {known?.map((u) => (
                <button
                  key={u.membershipId + u.kind}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl p-3 text-left ring-1 ring-slate-200 hover:bg-slate-50"
                  onClick={() =>
                    go(
                      u.kind === 'pin'
                        ? { name: 'pin', username: u.loginId, displayName: u.displayName }
                        : { name: 'password', email: u.loginId, displayName: u.displayName },
                    )
                  }
                >
                  <span className="flex size-10 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-700">
                    {u.displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{u.displayName}</span>
                    <span className="block text-xs text-slate-500">{u.kind === 'pin' ? 'Empleado' : 'Dueño'}</span>
                  </span>
                </button>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button variant="secondary" onClick={() => go({ name: 'pin' })}>
                  Soy empleado
                </Button>
                <Button variant="secondary" onClick={() => go({ name: 'password' })}>
                  Soy el dueño
                </Button>
              </div>
            </div>
          )}

          {mode.name === 'pin' && (
            <div className="space-y-4">
              {mode.username ? (
                <p className="text-center font-medium">Hola, {mode.displayName}. Ingresá tu PIN.</p>
              ) : (
                <Field label="Usuario">
                  {(id) => (
                    <Input
                      id={id}
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  )}
                </Field>
              )}
              <PinPad value={pin} onChange={setPin} onSubmit={submitPin} disabled={busy} />
              <Button variant="secondary" block onClick={() => go({ name: 'pick' })}>
                Volver
              </Button>
            </div>
          )}

          {mode.name === 'password' && (
            <>
            <form className="space-y-4" onSubmit={submitPassword}>
              {mode.email ? (
                <p className="text-center font-medium">Hola, {mode.displayName}.</p>
              ) : (
                <Field label="Email">
                  {(id) => (
                    <Input id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  )}
                </Field>
              )}
              <Field label="Contraseña">
                {(id) => (
                  <PasswordInput
                    id={id}
                    autoComplete="current-password"
                    required
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" size="lg" block loading={busy}>
                Entrar
              </Button>
              <Button variant="secondary" block onClick={() => go({ name: 'pick' })}>
                Volver
              </Button>
            </form>
            <ForgotPassword email={mode.email ?? email} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
